import { useCallback, useMemo } from 'react';
import { TablePaginationConfig } from 'antd';
import { useRequestPromise } from 'hooks/useRequest';
import { listManifests } from 'store/manifests/client';
import { GetManifestsParams, GetManifestsSortParams } from 'store/manifests/types';
import useManifestsFilters from './useManifestsFilters';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import { ModalFilters } from '../dashboard/components/ManifestsFiltersModal';
import moment from 'moment';

const useManifestsTable = (additionalParams?: Partial<GetManifestsParams>) => {
    const {
        debouncedQuery,
        queryInputVal,
        appliedParams,
        tags,
        archived,
        setArchived,
        setPage,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
        setQueryInputVal,
        setDate,
        setOwnerId,
        setTeamId,
        setCreatedBy,
        setManifestStatuses,
    } = useManifestsFilters();

    const {
        data,
        isLoading: isLoadingManifests,
        refetch: refetchManifestsRaw,
    } = useRequestPromise(listManifests, { withSignal: true, args: [{ ...appliedParams, ...additionalParams }] }, [
        appliedParams,
        additionalParams?.archived,
    ]);

    const refetchManifests = useCallback(
        () => refetchManifestsRaw({ ...appliedParams, ...additionalParams }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [refetchManifestsRaw, appliedParams, JSON.stringify(additionalParams)]
    );

    const { manifestStatuses, teamId, ownerId, createdBy, page, size, exactDate } = appliedParams;

    const filters: ModalFilters = useMemo(() => {
        return { query: queryInputVal, manifestStatuses, teamId, ownerId, createdBy };
    }, [createdBy, manifestStatuses, ownerId, queryInputVal, teamId]);

    const pagination: TablePaginationConfig = useMemo(
        () => ({
            current: page + 1,
            pageSize: size,
            total: data?.total,
            position: ['bottomCenter'],
            showSizeChanger: true,
            hideOnSinglePage: true,
        }),
        [page, size, data?.total]
    );

    const date = useMemo(() => (exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined), [exactDate]);

    const handleFiltersChange = (patch: Partial<ModalFilters>) => {
        if (patch.query !== undefined) {
            setQueryInputVal(patch.query);
            setPage(0);
        }

        if (patch.manifestStatuses !== undefined) {
            setManifestStatuses(patch.manifestStatuses);
            setPage(0);
        }

        if (patch.teamId !== undefined) {
            setTeamId(patch.teamId);
            setPage(0);
        }

        if (patch.ownerId !== undefined) {
            setOwnerId(patch.ownerId);
            setPage(0);
        }

        if (patch.createdBy !== undefined) {
            setCreatedBy(patch.createdBy);
            setPage(0);
        }
    };

    const handleDateChange = useCallback(
        (val: moment.Moment | null) => {
            if (!val) {
                setDate(undefined);
                return;
            }
            setDate(val.format('YYYY-MM-DD'));
        },
        [setDate]
    );

    const handleTableChange = useCallback(
        (antPagination: any, _filters: any, sorter: any) => {
            const raw = getTableChangeParams({ pagination: antPagination, sorter });

            const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                page?: number;
                size?: number;
                sortParameter?: string;
                sortDirection?: SortDirection;
            };

            setPage((p) => (typeof page === 'number' ? page : p));
            setSize((s) => (typeof size === 'number' ? size : s));

            if (!sortParameter || !sortDirection) {
                setSortState({ sort: undefined, sortDirection: undefined });
            } else {
                setSortState({
                    sort: sortParameter as GetManifestsSortParams,
                    sortDirection: sortDirection as SortDirections,
                });
            }
        },
        [setPage, setSize, setSortState]
    );

    return {
        debouncedQuery,
        queryInputVal,
        appliedParams,
        tags,
        archived,
        setArchived,
        setPage,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
        setQueryInputVal,
        setDate,
        setOwnerId,
        setTeamId,
        setCreatedBy,
        setManifestStatuses,

        date,
        filters,
        pagination,
        data,
        isLoadingManifests,
        refetchManifests,
        handleTableChange,
        handleFiltersChange,
        handleDateChange,
    };
};

export default useManifestsTable;
